import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { FormGroup } from "@mui/material"
import { Input } from "iebt-zing-storybook"
import React from "react"
import { Col, Form, FormLabel, Row } from "react-bootstrap"
import { showToast } from "components/toast"
import { ROUTES } from "sharedKernel/constants"
import { CardSection } from "../../../scouting/OpportunityForm/styles"
import {
  MeetingRoomWeekdayKey,
  WEEKDAY_KEYS_BY_INDEX,
} from "domain/feature/meeting-room/shared/workingDays"
import { IMeetingRoomSettingsDays } from "../types"
import { CopyButton, DayCheckboxWrapper, DaysGrid, FieldGroup, FieldLabel, HelperText, LinkInputGroup, RequiredMark, SectionDivider, SectionTitle } from "./styles"
import { trans } from "sharedKernel/i18n"
import { minutesToTime, timeToMinutes } from "sharedKernel/utils/format"

export interface RulesOfUseTabData {
  publicSlug: string
  days: IMeetingRoomSettingsDays
  openTime: string
  closeTime: string
  meetingSpacesLimit: number
  meetingDaysLimit: number
  meetingMinLeadTime: number
  meetingMaxDuration: number
  meetingAutomaticApproval: boolean
  eventSpacesLimit: number
  eventDaysLimit: number
  eventMinLeadTime: number
  eventInterestDemonstration: boolean
}

interface RulesOfUseTabProps {
  formId: string
  data: RulesOfUseTabData
  onChange: (data: RulesOfUseTabData) => void
}

const WEEKDAY_LABELS: Record<MeetingRoomWeekdayKey, string> = {
  sunday: 'Domingo',
  monday: 'Segunda',
  tuesday: 'Terça',
  wednesday: 'Quarta',
  thursday: 'Quinta',
  friday: 'Sexta',
  saturday: 'Sábado',
}

const WEEK_DAYS = WEEKDAY_KEYS_BY_INDEX.map(key => ({ key, label: WEEKDAY_LABELS[key] }))

const buildPublicLink = (publicSlug: string): string => {
  const slug = publicSlug.trim()
  if (slug.startsWith('http://') || slug.startsWith('https://')) {
    return slug
  }
  if (slug.startsWith('/')) {
    return `${window.location.origin}${slug}`
  }
  if (slug !== '') {
    return `${window.location.origin}/${slug}`
  }
  return `${window.location.origin}${ROUTES.PUBLIC.MEETING_ROOM_RESERVATION}`
}

export const RulesOfUseTab = ({ formId, data, onChange }: RulesOfUseTabProps): JSX.Element => {
  const labels = {
    generalRules: trans('meetingRoom.settings.form.generalRules'),
    publicAccessPageLink: trans('meetingRoom.settings.form.publicAccessPageLink'),
    copyLink: trans('meetingRoom.settings.form.copyLink'),
    daysOfOperation: trans('meetingRoom.settings.form.daysOfOperation'),
    startTime: trans('meetingRoom.settings.form.startTime'),
    endTime: trans('meetingRoom.settings.form.endTime'),
    reunionRules: trans('meetingRoom.settings.form.reunionRules'),
    eventsRules: trans('meetingRoom.settings.form.eventsRules'),
    limitOfSpacesPerRequest: trans('meetingRoom.settings.form.limitOfSpacesPerRequest'),
    limitOfDaysPerRequest: trans('meetingRoom.settings.form.limitOfDaysPerRequest'),
    minimumAntecedenceInDays: trans('meetingRoom.settings.form.minimumAntecedenceInDays'),
    maximumDurationForRequest: trans('meetingRoom.settings.form.maximumDurationForRequest'),
    limitOfSpacesPerRequestDescription: trans('meetingRoom.settings.form.limitOfSpacesPerRequestDescription'),
    limitOfDaysPerRequestDescription: trans('meetingRoom.settings.form.limitOfDaysPerRequestDescription'),
    minimumAntecedenceInDaysDescription: trans('meetingRoom.settings.form.minimumAntecedenceInDaysDescription'),
    // reunionsAutomaticApproval: trans('meetingRoom.settings.form.reunionsAutomaticApproval'),
    // eventsDemonstrationOfInterest: trans('meetingRoom.settings.form.eventsDemonstrationOfInterest'),
    // eventsRulesDescription: trans('meetingRoom.settings.form.eventsRulesDescription'),
  }

  const updateField = <K extends keyof RulesOfUseTabData>(field: K, value: RulesOfUseTabData[K]) => {
    onChange({ ...data, [field]: value })
  }

  const toggleDay = (key: keyof IMeetingRoomSettingsDays) => {
    onChange({
      ...data,
      days: { ...data.days, [key]: !data.days[key] },
    })
  }

  const handleCopyLink = async (): Promise<void> => {
    const link = buildPublicLink(data.publicSlug)
    try {
      await navigator.clipboard.writeText(link)
      showToast('success', 'Link copiado!')
    } catch {
      showToast('error', 'Não foi possível copiar o link')
    }
  }

  return (
    <Form id={formId} onSubmit={e => e.preventDefault()}>
      <CardSection>
        <Col sm={10}>
          <LinkInputGroup>
            <Input
              label={labels.publicAccessPageLink}
              isRequired
              showCounter={false}
              value={data.publicSlug}
              onChange={e => updateField('publicSlug', e.target.value)}
            />
            <CopyButton type="button" onClick={() => { void handleCopyLink() }}>
              <FontAwesomeIcon icon={['fas', 'link']} />
              {labels.copyLink}
            </CopyButton>
          </LinkInputGroup>
        </Col>

        <SectionTitle>{labels.generalRules}</SectionTitle>
        <FormGroup>
          <FormLabel>{labels.daysOfOperation}</FormLabel>
          <DaysGrid>
            {WEEK_DAYS.map(({ key, label }) => (
              <DayCheckboxWrapper key={key}>
                <Form.Check
                  type="checkbox"
                  id={key}
                  checked={data.days[key]}
                  onChange={() => toggleDay(key)}
                  label={label}
                />
              </DayCheckboxWrapper>
            ))}
          </DaysGrid>

          <Row>
          <Col sm={5}>
            <FormGroup>
              <Input type="time"
                isRequired
                label={labels.startTime}
                value={data.openTime}
                showCounter={false}
                onChange={e => updateField('openTime', e.target.value)}
              />
            </FormGroup>

          </Col>
          <Col sm={5}>
            <FormGroup>
              <Input
                label={labels.endTime}
                value={data.closeTime}
                type="time"
                isRequired
                showCounter={false}
                onChange={e => updateField('closeTime', e.target.value)}
              />
            </FormGroup>
          </Col>
          </Row>

          <SectionDivider />
          <SectionTitle>{labels.reunionRules}</SectionTitle>
          <Col sm={6}>
            <FieldGroup>
              <FieldLabel>{labels.limitOfSpacesPerRequest} <RequiredMark>*</RequiredMark></FieldLabel>
              <HelperText>{labels.limitOfSpacesPerRequestDescription}</HelperText>
              <Input
                type="number"
                isRequired
                showCounter={false}
                value={data.meetingSpacesLimit}
                onChange={e => updateField('meetingSpacesLimit', parseInt(e.target.value))}
              />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>{labels.limitOfDaysPerRequest} <RequiredMark>*</RequiredMark></FieldLabel>
              <HelperText>{labels.limitOfDaysPerRequestDescription}</HelperText>
              <Input
                type="number"
                isRequired
                showCounter={false}
                value={data.meetingDaysLimit}
                onChange={e => updateField('meetingDaysLimit', parseInt(e.target.value))}
              />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>{labels.minimumAntecedenceInDays} <RequiredMark>*</RequiredMark></FieldLabel>
              <HelperText>{labels.minimumAntecedenceInDaysDescription}</HelperText>
              <Input
                type="number"
                isRequired
                showCounter={false}
                value={data.meetingMinLeadTime}
                onChange={e => updateField('meetingMinLeadTime', parseInt(e.target.value))}
              />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>{labels.maximumDurationForRequest} <RequiredMark>*</RequiredMark></FieldLabel>
              <Input
                type="time"
                isRequired
                showCounter={false}
                value={minutesToTime(data.meetingMaxDuration)}
                onChange={e => updateField('meetingMaxDuration', timeToMinutes(e.target.value))}
              />
            </FieldGroup>
          </Col>

          {/* <ToggleContainer>
            <Form.Check
              type="switch"
              id="automatic-approval-switch"
              label={labels.reunionsAutomaticApproval}
              checked={data.meetingAutomaticApproval}
              onChange={e => updateField('meetingAutomaticApproval', e.target.checked)}
            />
          </ToggleContainer> */}

          <SectionDivider />
          <SectionTitle>{labels.eventsRules}</SectionTitle>
          <Col sm={6}>
            <FieldGroup>
              <FieldLabel>
                {labels.limitOfSpacesPerRequest} <RequiredMark>*</RequiredMark>
              </FieldLabel>
              <HelperText>{labels.limitOfSpacesPerRequestDescription}</HelperText>
              <Input
                type="number"
                isRequired
                showCounter={false}
                value={data.eventSpacesLimit}
                onChange={e => updateField('eventSpacesLimit', parseInt(e.target.value))}
              />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>{labels.limitOfDaysPerRequest} <RequiredMark>*</RequiredMark></FieldLabel>
              <HelperText>{labels.limitOfDaysPerRequestDescription}</HelperText>
              <Input
                type="number"
                isRequired
                showCounter={false}
                value={data.eventDaysLimit}
                onChange={e => updateField('eventDaysLimit', parseInt(e.target.value))}
              />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>{labels.minimumAntecedenceInDays} <RequiredMark>*</RequiredMark></FieldLabel>
              <HelperText>{labels.minimumAntecedenceInDaysDescription}</HelperText>
              <Input
                type="number"
                isRequired
                showCounter={false}
                value={data.eventMinLeadTime}
                onChange={e => updateField('eventMinLeadTime', parseInt(e.target.value))}
              />
            </FieldGroup>
          </Col>

          {/* <ToggleContainer>
            <Form.Check
              type="switch"
              id="interest-demonstration-switch"
              label={labels.eventsDemonstrationOfInterest}
              checked={data.eventInterestDemonstration}
              onChange={e => updateField('eventInterestDemonstration', e.target.checked)}
            />
          </ToggleContainer> */}

          {/* <InformationBox>
            <InformationIcon icon={['fas', 'info-circle']}/>
            <InformationText>{labels.eventsRulesDescription}</InformationText>
          </InformationBox> */}
        </FormGroup>
      </CardSection>
    </Form>
  )
}
